import { DEFAULT_COUNTRY, normalizeCountryLabel } from "../../../../../_shared/_logic/countries"

export type ContactAddressValue = {
  street: string
  house_number: string
  zip: string
  city: string
  country: string
  address_extra: string
}

type ContactAddressRecord = Partial<Record<keyof ContactAddressValue, unknown>>

export const emptyContactAddress = (): ContactAddressValue => ({
  street: "",
  house_number: "",
  zip: "",
  city: "",
  country: DEFAULT_COUNTRY,
  address_extra: "",
})

const clean = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()

const contactAddressFromRecord = (value: ContactAddressRecord): ContactAddressValue => ({
  street: clean(value.street),
  house_number: clean(value.house_number),
  zip: clean(value.zip),
  city: clean(value.city),
  country: normalizeCountryLabel(clean(value.country)),
  address_extra: clean(value.address_extra),
})

export const parseContactAddress = (
  rawValue: string | ContactAddressValue | ContactAddressRecord | null | undefined,
): ContactAddressValue => {
  if (!rawValue) return emptyContactAddress()

  if (typeof rawValue === "object" && !Array.isArray(rawValue))
    return contactAddressFromRecord(rawValue)

  if (typeof rawValue !== "string") return emptyContactAddress()

  try {
    const parsed = JSON.parse(rawValue)

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return emptyContactAddress()

    return contactAddressFromRecord(parsed)
  } catch {
    return emptyContactAddress()
  }
}

export const serializeContactAddress = (address: ContactAddressValue): string =>
  JSON.stringify({
    street: clean(address.street),
    house_number: clean(address.house_number),
    zip: clean(address.zip),
    city: clean(address.city),
    country: normalizeCountryLabel(address.country),
    address_extra: clean(address.address_extra),
  })

export const formatContactAddress = (
  rawValue: string | ContactAddressValue | ContactAddressRecord | null | undefined,
): string => {
  const address = parseContactAddress(rawValue)
  const streetLine = [address.street, address.house_number].map(clean).filter(Boolean).join(" ")
  const cityLine = [address.zip, address.city].map(clean).filter(Boolean).join(" ")

  return [streetLine, cityLine, address.address_extra, normalizeCountryLabel(address.country)]
    .map(clean)
    .filter(Boolean)
    .join(", ")
}

export const isValidContactAddress = (address: ContactAddressValue): boolean =>
  Boolean(clean(address.street) && clean(address.zip) && clean(address.city))
